import { type RefObject, useEffect, useRef } from "react";
import { initialFocusTarget, tabbableIn, wrapFocus } from "./focusTrap";

interface ModalBehaviourOptions {
  open: boolean;
  dialogRef: RefObject<HTMLDivElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/** While open: scroll lock, initial focus, Escape closes, Tab stays inside; then focus goes back. */
export function useModalBehaviour({ open, dialogRef, initialFocusRef, onClose }: ModalBehaviourOptions): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const restoreFocus = rememberFocus();
    const unlockScroll = lockBodyScroll();
    const cancelInitialFocus = focusOnNextFrame(() => {
      const dialog = dialogRef.current;
      if (dialog) initialFocusTarget(dialog, initialFocusRef).focus();
    });

    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) trapTab(e, dialogRef.current);
    }

    document.addEventListener("keydown", onKey);
    return () => {
      cancelInitialFocus();
      document.removeEventListener("keydown", onKey);
      unlockScroll();
      restoreFocus();
    };
  }, [open, initialFocusRef]); // eslint-disable-line react-hooks/exhaustive-deps
}

function rememberFocus(): () => void {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  return () => previouslyFocused?.focus?.();
}

function lockBodyScroll(): () => void {
  const { overflow } = document.body.style;
  document.body.style.overflow = "hidden";
  return () => {
    document.body.style.overflow = overflow;
  };
}

/** Waits a frame so the dialog is in the page before focusing inside it. */
function focusOnNextFrame(focus: () => void): () => void {
  const frame = requestAnimationFrame(focus);
  return () => cancelAnimationFrame(frame);
}

function trapTab(e: KeyboardEvent, dialog: HTMLElement): void {
  const focused = document.activeElement;
  const target = wrapFocus(tabbableIn(dialog, focused), focused, e.shiftKey);
  if (!target) return;
  e.preventDefault();
  target.focus();
}
